'use es6';

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import { byName as TemplateTypes } from 'ContentUtils/constants/TemplateTypes';
import { getInfoById, getSuccessesById, makeGetPrimaryErrors, makeGetPrimaryWarnings, makeGetPrimaryInfo, makeGetPrimarySuccesses, makeGetLintedJavaScriptMessages, makeGetLintedCssMessages, makeGetCombinedErrorsById, makeGetCombinedWarningsById, getEslintDemoMessages } from 'DesignData/selectors/editorErrors';
import { isCodeEditorActive as getIsCodeEditorActive, isRawAssetEditorActive as getIsRawAssetEditorActive, isCustomWidgetEditorActive as getIsCustomWidgetEditorActive, isLayoutEditorActive as getIsLayoutEditorActive, getIsSettingsEditorActive, getActiveSettingsEditorCategory } from 'DesignData/selectors/app';
import { getTemplateType } from 'DesignData/selectors/templateBuffers';
import { getShowCompiledSplitScreen } from 'DesignData/selectors/codeEditor';
import { getEslintConfigError } from 'DesignData/selectors/javaScriptLinter';
import { JSLINT } from 'DesignData/enums/settings/Categories';
import { OPEN_STATUS_BAR_DRAWER } from 'DesignData/enums/FlashKeys';
import { EMPTY_ARRAY } from 'DesignData/lib/array';
export const getCodemirrorSessionId = state => state.statusBar.codemirrorSessionId;
export const getCodemirrorType = state => state.statusBar.codemirrorType;
export const getActiveLanguage = state => state.statusBar.activeLanguage;
export const getIsOpenDrawerRequested = state => state.flash[OPEN_STATUS_BAR_DRAWER];
const EMPTY_MESSAGE_DATA = SeamlessImmutable({
  errorsCount: 0,
  warningsCount: 0,
  infosCount: 0,
  successesCount: 0,
  hasMessages: false,
  hasPrimaryMessages: false,
  hasSplitMessages: false,
  showSplitScreen: false,
  primaryErrors: EMPTY_ARRAY,
  primaryWarnings: EMPTY_ARRAY,
  primaryInfo: EMPTY_ARRAY,
  primarySuccesses: EMPTY_ARRAY,
  splitErrors: EMPTY_ARRAY,
  splitWarnings: EMPTY_ARRAY,
  splitInfo: EMPTY_ARRAY,
  splitSuccesses: EMPTY_ARRAY
});

function createMessagesSummary(messages = {}) {
  const errors = messages && messages.errors || EMPTY_ARRAY;
  const warnings = messages && messages.warnings || EMPTY_ARRAY;
  const info = messages && messages.info || EMPTY_ARRAY;
  const successes = messages && messages.successes || EMPTY_ARRAY;
  const errorsCount = errors.length;
  const warningsCount = warnings.length;
  const infosCount = info.length;
  const successesCount = successes.length;
  return {
    errors,
    warnings,
    info,
    successes,
    errorsCount,
    warningsCount,
    infosCount,
    successesCount,
    hasMessages: !!(errorsCount || warningsCount || infosCount || successesCount)
  };
}

function createCombinedMessagesSummary(primaryMessages, splitMessages, showSplitScreen) {
  const primary = createMessagesSummary(primaryMessages);
  const split = createMessagesSummary(splitMessages);

  if (!(primary.hasMessages || showSplitScreen !== EMPTY_MESSAGE_DATA.showSplitScreen || showSplitScreen && split.hasMessages)) {
    return EMPTY_MESSAGE_DATA;
  }

  const result = Object.assign({}, EMPTY_MESSAGE_DATA, {
    showSplitScreen
  });

  if (primary.hasMessages) {
    Object.assign(result, {
      hasMessages: true,
      hasPrimaryMessages: true,
      errorsCount: primary.errorsCount,
      warningsCount: primary.warningsCount,
      infosCount: primary.infosCount,
      successesCount: primary.successesCount,
      primaryErrors: primary.errors,
      primaryWarnings: primary.warnings,
      primaryInfo: primary.info,
      primarySuccesses: primary.successes
    });
  }

  if (showSplitScreen && split.hasMessages) {
    Object.assign(result, {
      hasSplitMessages: true,
      hasMessages: true,
      errorsCount: result.errorsCount + split.errorsCount,
      warningsCount: result.warningsCount + split.warningsCount,
      infosCount: result.infosCount + split.infosCount,
      successesCount: result.successesCount + split.successesCount,
      splitErrors: split.errors,
      splitWarnings: split.warnings,
      splitInfo: split.info,
      splitSuccesses: split.successes
    });
  }

  return result;
}
/**
 * Required Props
 *  - assetId
 */


const makeGetRawAssetEditorMessageData = () => {
  const getPrimaryErrors = makeGetPrimaryErrors();
  const getLintedJavaScriptMessages = makeGetLintedJavaScriptMessages();
  let data = EMPTY_MESSAGE_DATA;
  return createSelector([getIsRawAssetEditorActive, getLintedJavaScriptMessages, getPrimaryErrors], (isRawAssetEditorActive, lintedJavaScriptMessages, primaryErrors) => {
    if (!isRawAssetEditorActive) {
      return EMPTY_MESSAGE_DATA;
    }

    const errors = lintedJavaScriptMessages.errors.concat(primaryErrors);
    const messages = Object.assign({}, lintedJavaScriptMessages, {
      errors
    });
    const updates = createCombinedMessagesSummary(messages, null, false);

    if (updates === EMPTY_MESSAGE_DATA) {
      return data = EMPTY_MESSAGE_DATA;
    }

    data = SeamlessImmutable.merge(data, updates, {
      deep: true
    });
    return data;
  });
};
/**
 * Required Props
 *  - assetId
 */


const makeGetCodeEditorMessageData = () => {
  const getPrimaryErrors = makeGetPrimaryErrors();
  const getPrimaryWarnings = makeGetPrimaryWarnings();
  const getPrimaryInfo = makeGetPrimaryInfo();
  const getPrimarySuccesses = makeGetPrimarySuccesses();
  const getLintedJavaScriptMessages = makeGetLintedJavaScriptMessages();
  const getLintedCssMessages = makeGetLintedCssMessages();
  let data = EMPTY_MESSAGE_DATA;
  return createSelector([getIsCodeEditorActive, getTemplateType, getShowCompiledSplitScreen, getPrimaryErrors, getPrimaryWarnings, getPrimaryInfo, getPrimarySuccesses, getLintedJavaScriptMessages, getLintedCssMessages], (isCodeEditorActive, templateType, showCompiledSplitScreen, primaryErrors, primaryWarnings, primaryInfo, primarySuccesses, lintedJavaScriptMessages, lintedCssMessages) => {
    if (!isCodeEditorActive) {
      return EMPTY_MESSAGE_DATA;
    }

    const primary = {
      errors: primaryErrors,
      warnings: primaryWarnings,
      info: primaryInfo,
      successes: primarySuccesses
    };
    let split;

    if (showCompiledSplitScreen) {
      switch (templateType) {
        case TemplateTypes.css.id:
          split = lintedCssMessages;
          break;

        case TemplateTypes.js.id:
          split = lintedJavaScriptMessages;
          break;

        default:
          break;
      }
    }

    const updates = createCombinedMessagesSummary(primary, split, showCompiledSplitScreen);

    if (updates === EMPTY_MESSAGE_DATA) {
      return EMPTY_MESSAGE_DATA;
    }

    data = SeamlessImmutable.merge(data, updates);
    return data;
  });
};
/**
 *
 */


const getSettingsEditorMessageData = (() => {
  let data = EMPTY_MESSAGE_DATA;
  return createSelector([getIsSettingsEditorActive, getActiveSettingsEditorCategory, getEslintConfigError, getEslintDemoMessages], (isSettingsEditorActive, settingsCategory, eslintConfigError, eslintDemoMessages) => {
    if (!isSettingsEditorActive) {
      return EMPTY_MESSAGE_DATA;
    }

    switch (settingsCategory) {
      case JSLINT:
        {
          const primary = eslintConfigError ? {
            errors: [eslintConfigError]
          } : undefined;
          const split = eslintDemoMessages;
          const updates = createCombinedMessagesSummary(primary, split, true);

          if (updates === EMPTY_MESSAGE_DATA) {
            data = EMPTY_MESSAGE_DATA;
          }

          data = updates === EMPTY_MESSAGE_DATA ? EMPTY_MESSAGE_DATA : SeamlessImmutable.merge(EMPTY_MESSAGE_DATA, updates);
          break;
        }

      default:
        data = EMPTY_MESSAGE_DATA;
        break;
    }

    return data;
  });
})();
/**
 * Selector for all editors other than the Code Editor (at this time).
 *
 * Required Props
 *  - assetId
 */


const makeGetEditorMessageData = () => {
  const getCombinedErrorsById = makeGetCombinedErrorsById();
  const getCombinedWarningsById = makeGetCombinedWarningsById();
  let data = EMPTY_MESSAGE_DATA;
  return createSelector([getIsCustomWidgetEditorActive, getIsLayoutEditorActive, getCombinedErrorsById, getCombinedWarningsById, getInfoById, getSuccessesById], (isCustomWidgetEditorActive, isLayoutEditorActive, primaryErrors, primaryWarnings, primaryInfo, primarySuccesses) => {
    if (!(isCustomWidgetEditorActive || isLayoutEditorActive)) {
      return EMPTY_MESSAGE_DATA;
    }

    const primary = {
      errors: primaryErrors,
      warnings: primaryWarnings,
      info: primaryInfo,
      successes: primarySuccesses
    };
    const updates = createCombinedMessagesSummary(primary, undefined, false);

    if (updates === EMPTY_MESSAGE_DATA) {
      return EMPTY_MESSAGE_DATA;
    }

    data = SeamlessImmutable.merge(data, updates);
    return data;
  });
};
/**
 * Get the EditorError data for the current asset.
 *
 * Required Props
 *  - assetId
 */


export const makeGetEditorErrorData = () => {
  const getCodeEditorMessageData = makeGetCodeEditorMessageData();
  const getRawAssetEditorMessageData = makeGetRawAssetEditorMessageData();
  const getEditorMessageData = makeGetEditorMessageData();
  return createSelector([getIsRawAssetEditorActive, getIsCodeEditorActive, getIsCustomWidgetEditorActive, getIsLayoutEditorActive, getIsSettingsEditorActive, getCodeEditorMessageData, getSettingsEditorMessageData, getEditorMessageData, getRawAssetEditorMessageData], (isRawAssetEditorActive, isCodeEditorActive, isCustomWidgetEditorActive, isLayoutEditorActive, isSettingsEditorActive, codeEditorMessageData, settingsEditorMessageData, editorMessageData, rawAssetEditorMessageData) => {
    if (isRawAssetEditorActive) {
      return rawAssetEditorMessageData;
    }

    if (isCodeEditorActive) {
      return codeEditorMessageData;
    }

    if (isCustomWidgetEditorActive || isLayoutEditorActive) {
      return editorMessageData;
    }

    if (isSettingsEditorActive) {
      return settingsEditorMessageData;
    }

    return EMPTY_MESSAGE_DATA;
  });
};